import { useEnvironmentVariables } from "#app/EnvironmentProvider.tsx";
import { StaggeredAnimation } from "#components/animation/StaggeredAnimation.tsx";
import { StaggeredAnimationItem } from "#components/animation/StaggeredAnimationItem.tsx";
import { useGetChannelsDetailsSuspenseQuery } from "#features/channels/api/useGetChannelsDetailsSuspenseQuery.ts";
import { ChannelTitle } from "#features/channels/components/channels-details/ChannelTitle.tsx";
import { ConnectedPersonalChannel } from "#features/channels/components/channels-details/ConnectedPersonalChannel.tsx";
import { ConnectPersonalChannel } from "#features/channels/components/channels-details/ConnectPersonalChannel.tsx";
import { CustomMcpServerItem } from "#features/channels/components/channels-details/CustomMcpServerChannel.tsx";
import { OfficevibeChannel } from "#features/channels/components/channels-details/OfficevibeChannel.tsx";
import { WebsiteItem } from "#features/channels/components/channels-details/WebCrawlerChannel.tsx";
import { WpmChannel } from "#features/channels/components/channels-details/WpmChannel.tsx";
import { LinkItem } from "#features/channels/components/LinkItem.tsx";
import { channelTypeToIntegration } from "#features/conversations/integrations/index.ts";
import { useConnectedExternalApps } from "#hooks/useConnectedExternalApps.ts";
import { useFeatureFlag } from "#hooks/useFeatureFlag.ts";
import { useSharedTranslation } from "#hooks/useSharedTranslation.ts";
import { SettingsIcon } from "@hopper-ui/icons";
import { useMemo } from "react";

export const ChannelsDetails = () => {
    const { tShared } = useSharedTranslation();
    const { managementAppBaseUrl } = useEnvironmentVariables();
    const { allApps } = useConnectedExternalApps();
    const { data: {
        workleapChannelTypes,
        websites,
        personalIntegrations,
        customMcpServers,
        showKnowledgeSettings
    } } = useGetChannelsDetailsSuspenseQuery();
    const { hasFeatureFlag } = useFeatureFlag();

    // A flag flip can put the backend ahead of this build's manifests, and both personal-channel cards
    // render null without one — dropping those entries keeps the titles below off an empty list.
    const visiblePersonalIntegrations = useMemo(() => {
        const renderable = personalIntegrations.filter(x => channelTypeToIntegration[x.channelType]);

        return hasFeatureFlag("SharePointUserIntegration")
            ? renderable
            : renderable.filter(x => x.channelType !== "UserSharepoint");
    }, [hasFeatureFlag, personalIntegrations]);

    const showPlatformTitle = useMemo(() => workleapChannelTypes.length > 0, [workleapChannelTypes]);
    const hasAvailableAppsToConnect = useMemo(() => visiblePersonalIntegrations.some(x => x.canConnect), [visiblePersonalIntegrations]);
    const showConnectedAppsTitle = useMemo(() => visiblePersonalIntegrations.some(x => x.isConnected), [visiblePersonalIntegrations]);
    const showCustomMcpServers = useMemo(() => customMcpServers.length > 0, [customMcpServers]);
    const showWebsites = useMemo(() => allApps.includes("WebCrawler"), [allApps]);

    return (
        <StaggeredAnimation>
            {showPlatformTitle && (
                <StaggeredAnimationItem>
                    <ChannelTitle title={tShared("workleapPlatform")} />
                </StaggeredAnimationItem>
            )}
            {workleapChannelTypes.includes("WorkleapOfficevibe") && (
                <StaggeredAnimationItem>
                    <OfficevibeChannel />
                </StaggeredAnimationItem>
            )}
            {workleapChannelTypes.includes("WorkleapPerformance") && (
                <StaggeredAnimationItem>
                    <WpmChannel />
                </StaggeredAnimationItem>
            )}
            {hasAvailableAppsToConnect && (
                <StaggeredAnimationItem>
                    <ChannelTitle title={tShared("availableApps")} />
                </StaggeredAnimationItem>
            )}
            {visiblePersonalIntegrations.filter(x => x.canConnect).map(({ channelType }) => (
                <StaggeredAnimationItem key={channelType}>
                    <ConnectPersonalChannel channelType={channelType} />
                </StaggeredAnimationItem>
            ))}
            {showConnectedAppsTitle && (
                <StaggeredAnimationItem>
                    <ChannelTitle title={tShared("connectedApps")} />
                </StaggeredAnimationItem>
            )}
            {visiblePersonalIntegrations.filter(x => x.isConnected).map(({ channelType }) => (
                <StaggeredAnimationItem key={channelType}>
                    <ConnectedPersonalChannel channelType={channelType} />
                </StaggeredAnimationItem>
            ))}
            {showCustomMcpServers && (
                <StaggeredAnimationItem>
                    <ChannelTitle title={tShared("customMcpServers")} />
                </StaggeredAnimationItem>
            )}
            {customMcpServers.map(customMcpServer => (
                <StaggeredAnimationItem key={customMcpServer.definitionId}>
                    <CustomMcpServerItem customMcpServer={customMcpServer} />
                </StaggeredAnimationItem>
            ))}
            {showWebsites && (
                <StaggeredAnimationItem>
                    <ChannelTitle title={tShared("websites")} />
                </StaggeredAnimationItem>
            )}
            {websites.map(website => <StaggeredAnimationItem key={website.rootUrl}><WebsiteItem website={website} /></StaggeredAnimationItem>)}
            {showKnowledgeSettings && (
                <StaggeredAnimationItem>
                    <LinkItem
                        icon={<SettingsIcon color="neutral-weak" slot="start-icon" />}
                        title={tShared("knowledgeSettings")}
                        href={`${managementAppBaseUrl}/settings/ai/knowledge`}
                        color="neutral-weak"
                    />
                </StaggeredAnimationItem>
            )}
        </StaggeredAnimation>
    );
};
